/**
 * Client-logging policy state (remote UI log forwarding §5.12.4).
 *
 * Holds the server-delivered remote_logging policy plus the LOCAL safety
 * switches. Effective-on is any-off-wins: build flag AND active policy AND
 * no kill-switch AND breaker not latched. The kill-switch persists in
 * localStorage so a user on a flaky device can stop diagnostics for good
 * without waiting for staff (it deliberately survives logout and reload).
 */
import { defineStore } from 'pinia';
import type { RemoteLoggingPolicy } from '@shared/core/logging';

export const KILL_SWITCH_STORAGE_KEY = 'emalino.clientLogging.disabled';

function readKillSwitch(): boolean {
  try {
    return localStorage.getItem(KILL_SWITCH_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function buildFlagEnabled(): boolean {
  // Default-on: only the explicit string 'false' compiles the forwarder out.
  return import.meta.env.VITE_CLIENT_LOG_FORWARDING !== 'false';
}

export const useClientLoggingStore = defineStore('store-client-logging', {
  state: () => ({
    policy: null as RemoteLoggingPolicy | null,
    killSwitch: readKillSwitch(),
    breakerLatched: false,
  }),

  getters: {
    isPolicyActive(state): boolean {
      if (!state.policy?.enabled) return false;
      const expiresAt = state.policy.expires_at;
      if (!expiresAt) return true;
      const ts = new Date(expiresAt).getTime();
      return !Number.isFinite(ts) || ts > Date.now();
    },

    /** Any "off" wins (plan §5.12.4). */
    isEffectiveOn(state): boolean {
      return (
        buildFlagEnabled() &&
        !state.killSwitch &&
        !state.breakerLatched &&
        this.isPolicyActive
      );
    },
  },

  actions: {
    configure(policy: RemoteLoggingPolicy | null): void {
      this.policy = policy;
      // A fresh policy re-evaluates the breaker latch (the buffer resets its
      // breaker on a genuinely new policy; mirror that here).
      if (policy) this.breakerLatched = false;
    },

    clear(): void {
      this.policy = null;
      this.breakerLatched = false;
      // killSwitch intentionally survives logout: it is a device-level
      // safety switch, not user state.
    },

    setKillSwitch(disabled: boolean): void {
      this.killSwitch = disabled;
      try {
        localStorage.setItem(KILL_SWITCH_STORAGE_KEY, disabled ? 'true' : 'false');
      } catch {
        // Private mode / blocked storage: in-memory state still applies.
      }
    },

    markBreakerLatched(): void {
      this.breakerLatched = true;
    },
  },
});
